import { addBrandAlias } from '@/app/brands/brandCatalogActions';
import logger from '@/util/logger';
import { withResearchedDomainsAdded } from '@rock/brands';
import { BrandDao, BrandResearchDao, createId } from '@rock/db-admin';
import type { Brand, BrandRequest, BrandResearchId, BrandResearchProposal, Timestamp, UserId } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import {
    BrandRequestResolutionError,
    createBrandNamed,
    resolveAsAlias,
    resolveAsLine,
    resolveAsNotABrand,
} from './brandRequestActions';

const log = logger('brandResearchActions');
const brandDao = new BrandDao();
const brandResearchDao = new BrandResearchDao();

/**
 * Ask for an AI research run on a pending request (a few cents): the `requested` run doc is the whole request,
 * and the `onBrandResearchRequested` trigger picks it up and records its progress on the same doc.
 */
export async function requestBrandResearch(request: BrandRequest, by: UserId): Promise<void> {
    const researchId = createId<BrandResearchId>();
    await brandResearchDao.setBrandResearch({
        object: 'brand-research',
        schema: 1,
        id: researchId,
        requestId: request.id,
        name: request.brand,
        requestedBy: by,
        status: 'requested',
        calls: [],
        costUsd: 0,
        requestedAt: new Date().toISOString() as Timestamp,
    });
    log.info('Requested brand research', { requestId: request.id, researchId });
}

/** What the admin kept ticked: the aliases and domains to add along with the decision. */
export interface ResearchPicks {
    aliases: string[];
    domains: string[];
}

export interface AppliedResearch {
    brand?: Brand;
    /** Picks that could not be added, each a sentence for the admin. */
    skipped: string[];
}

/**
 * Apply a research proposal through the request page's own resolve flows: the decision first (so the request
 * settles and its waiting models and recalls fan out), then each picked alias and the picked domains onto the
 * brand it resolved to. A pick that can't be added is reported, never fatal: the decision has already landed.
 */
export async function applyBrandResearch(
    request: BrandRequest,
    proposal: BrandResearchProposal,
    picks: ResearchPicks,
    brands: readonly Brand[],
    by: UserId,
): Promise<AppliedResearch> {
    if (proposal.decision === 'not-a-brand') {
        await resolveAsNotABrand(request, proposal.notABrandReason ?? proposal.summary, by);
        return { skipped: [] };
    }
    const brand = await resolveDecision(request, proposal, brands, by);
    const skipped = await addAliases(brand, picks.aliases, by);
    await addDomains(brand, picks.domains);
    log.info('Applied brand research', { requestId: request.id, brandId: brand.id, decision: proposal.decision });
    return { brand, skipped };
}

/** Whether Apply adds this alias without a pick: the requested name, on a proposal landing under another name. */
export function joinsOnApply(
    aliasName: string,
    request: Pick<BrandRequest, 'brand'>,
    proposal: Pick<BrandResearchProposal, 'decision' | 'brandName'>,
): boolean {
    if (proposal.decision === 'not-a-brand' || !isSameBrandName(aliasName, request.brand)) return false;
    return proposal.decision !== 'new-brand' || !isSameBrandName(proposal.brandName ?? request.brand, request.brand);
}

async function resolveDecision(
    request: BrandRequest,
    proposal: BrandResearchProposal,
    brands: readonly Brand[],
    by: UserId,
): Promise<Brand> {
    if (proposal.decision === 'alias-of') return resolveAsAlias(request, catalogBrand(proposal, brands), by);
    if (proposal.decision === 'line-of') {
        const brand = proposal.targetBrandId
            ? catalogBrand(proposal, brands)
            : await createBrandNamed(proposal.targetBrandName ?? '', by, proposal.targetBrandName);
        return resolveAsLine(request, brand, proposal.lineName ?? request.brand, by);
    }
    // Creating the brand under the requested name settles the request; under another name ("PaceMaster" for
    // AEROBICS), the requested name then joins it as an alias.
    const brandName = proposal.brandName ?? request.brand;
    const brand = await createBrandNamed(brandName, by, proposal.brandName);
    if (isSameBrandName(brandName, request.brand)) return brand;
    return resolveAsAlias(request, brand, by);
}

function isSameBrandName(a: string, b: string): boolean {
    return normalizeBrandName(a).key === normalizeBrandName(b).key;
}

function catalogBrand(proposal: BrandResearchProposal, brands: readonly Brand[]): Brand {
    const brand = brands.find((candidate) => candidate.id === proposal.targetBrandId);
    if (!brand) throw new BrandRequestResolutionError('The brand the research named is no longer in the catalog.');
    return brand;
}

/** Each alias against the brand as it stands after the last one, since every addition rewrites its alias list. */
async function addAliases(brand: Brand, aliases: readonly string[], by: UserId): Promise<string[]> {
    const skipped: string[] = [];
    for (const alias of aliases) {
        const current = (await brandDao.getBrand(brand.id)) ?? brand;
        const outcome = await addBrandAlias(current, alias, by);
        if (outcome.status === 'taken') skipped.push(`“${alias}” already belongs to ${outcome.brand.canonicalName}.`);
        if (outcome.status === 'refused') skipped.push(outcome.reason);
    }
    return skipped;
}

async function addDomains(brand: Brand, domains: readonly string[]): Promise<void> {
    if (domains.length === 0) return;
    const current = (await brandDao.getBrand(brand.id)) ?? brand;
    const next = withResearchedDomainsAdded(current, domains);
    if (next) await brandDao.setBrandResearchedDomains(brand.id, next);
}
